import React, { useState, useEffect } from 'react';
import type { Meal, MealTarget } from '../models/diary';
import { mealService } from '../services/mealService';
import { computeMealMacros, sumMealsMacros } from '../utils/mealMacros';
import SearchableSelect from './SearchableSelect';
import MealEditorModal from './MealEditorModal';

interface MealItemFormProps {
  meals: Meal[];
  mealTarget?: MealTarget;
  // Patient profile id (the same value used for /diary-items/ — NOT the User id).
  patientId?: string | number;
  onMealsChange: (meals: Meal[]) => void;
  onMealTargetChange: (target: MealTarget) => void;
  isEditMode?: boolean;
  onDelete?: () => void;
}

const TARGET_FIELDS: { key: keyof MealTarget; label: string; unit: string; required?: boolean }[] = [
  { key: 'calories', label: 'Calorías', unit: 'kcal', required: true },
  { key: 'protein', label: 'Proteínas', unit: 'g' },
  { key: 'carbs', label: 'Carbohidratos', unit: 'g' },
  { key: 'fat', label: 'Grasas', unit: 'g' },
  { key: 'fiber', label: 'Fibra', unit: 'g' },
  { key: 'water', label: 'Agua', unit: 'ml' },
];

const MealItemForm: React.FC<MealItemFormProps> = ({
  meals,
  mealTarget,
  patientId,
  onMealsChange,
  onMealTargetChange,
  isEditMode = false,
  onDelete,
}) => {
  const [patientMeals, setPatientMeals] = useState<Meal[]>([]);
  const [loadingMeals, setLoadingMeals] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingMeal, setEditingMeal] = useState<Meal | null>(null);

  const fetchMeals = async () => {
    if (!patientId) return;
    setLoadingMeals(true);
    try {
      const response = await mealService.getAll(patientId, 1, 200);
      setPatientMeals(response.results);
    } catch (e) {
      console.error('Error fetching patient meals:', e);
    } finally {
      setLoadingMeals(false);
    }
  };

  useEffect(() => {
    fetchMeals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const attachedIds = new Set(meals.map(m => m.id));
  const availableMeals = patientMeals.filter(m => !attachedIds.has(m.id));

  const handleAttach = (mealId: number) => {
    const meal = patientMeals.find(m => m.id === mealId);
    if (meal && !attachedIds.has(meal.id)) {
      onMealsChange([...meals, meal]);
    }
    setShowPicker(false);
  };

  const handleDetach = (mealId: number) => {
    onMealsChange(meals.filter(m => m.id !== mealId));
  };

  const handleMealSaved = (saved: Meal) => {
    // Refresh the patient meal library and reflect the change in the attached list.
    fetchMeals();
    if (editingMeal) {
      onMealsChange(meals.map(m => (m.id === saved.id ? saved : m)));
    } else {
      onMealsChange([...meals.filter(m => m.id !== saved.id), saved]);
    }
    setEditorOpen(false);
    setEditingMeal(null);
  };

  const handleTargetField = (key: keyof MealTarget, value: string) => {
    const base: MealTarget = {
      calories: mealTarget?.calories ?? '',
      protein: mealTarget?.protein ?? '',
      carbs: mealTarget?.carbs ?? '',
      fat: mealTarget?.fat ?? '',
      fiber: mealTarget?.fiber ?? '',
      water: mealTarget?.water ?? '',
      ...(mealTarget?.id ? { id: mealTarget.id } : {}),
    };
    onMealTargetChange({ ...base, [key]: value });
  };

  const handleSumFromMeals = () => {
    const totals = sumMealsMacros(meals);
    onMealTargetChange({
      ...(mealTarget?.id ? { id: mealTarget.id } : {}),
      calories: totals.calories,
      protein: totals.protein,
      carbs: totals.carbs,
      fat: totals.fat,
      fiber: totals.fiber,
      water: totals.water,
    });
  };

  const targetValue = (key: keyof MealTarget): string => {
    const v = mealTarget?.[key];
    return v === undefined || v === null ? '' : String(v);
  };

  return (
    <div style={{ marginBottom: 20 }}>
      {/* Section header */}
      <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '2px solid #e5e7eb' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#1f2937' }}>Comida</h3>
            <p style={{ margin: '4px 0 0 0', fontSize: 13, color: '#6b7280' }}>
              Define el objetivo de macros del momento y adjunta comidas recomendadas
            </p>
          </div>
          {isEditMode && onDelete && (
            <button
              type="button"
              onClick={onDelete}
              style={{ background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
              onMouseEnter={e => (e.currentTarget.style.background = '#fecaca')}
              onMouseLeave={e => (e.currentTarget.style.background = '#fee2e2')}
            >
              <span>🗑️</span>
              <span>Eliminar</span>
            </button>
          )}
        </div>
      </div>

      {/* Meal target */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>1. Objetivo de macros *</span>
          <button
            type="button"
            onClick={handleSumFromMeals}
            disabled={meals.length === 0}
            style={{ background: 'none', border: 'none', color: meals.length === 0 ? '#9ca3af' : '#38b2ac', fontSize: 13, fontWeight: 600, cursor: meals.length === 0 ? 'not-allowed' : 'pointer' }}
            title="Suma los macros de las comidas adjuntas"
          >
            Σ Sumar desde comidas
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {TARGET_FIELDS.map(({ key, label, unit, required }) => (
            <label key={key as string} style={{ display: 'block' }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>
                {label} ({unit}){required && <span style={{ color: '#dc2626' }}> *</span>}
              </span>
              <input
                type="number"
                min="0"
                step="any"
                value={targetValue(key)}
                onChange={e => handleTargetField(key, e.target.value)}
                style={{
                  width: '100%', padding: '9px 10px',
                  border: required && !targetValue(key) ? '2px solid #fbbf24' : '1px solid #cbd5e1',
                  borderRadius: 6, fontSize: 14, outline: 'none', boxSizing: 'border-box',
                }}
                placeholder="0"
              />
            </label>
          ))}
        </div>
      </div>

      {/* Attached meals */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>2. Comidas recomendadas</span>
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              type="button"
              onClick={() => setShowPicker(v => !v)}
              style={{ background: 'none', border: 'none', color: '#38b2ac', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              + Añadir comida
            </button>
            <button
              type="button"
              onClick={() => { setEditingMeal(null); setEditorOpen(true); }}
              style={{ background: 'none', border: 'none', color: '#38b2ac', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              + Crear comida nueva
            </button>
          </div>
        </div>

        {showPicker && (
          <div style={{ marginBottom: 12 }}>
            <SearchableSelect
              options={availableMeals.map(m => ({ value: m.id, label: `${m.name} (${m.calories} kcal)`, searchText: m.name }))}
              value={''}
              onChange={(value) => handleAttach(Number(value))}
              placeholder={loadingMeals ? 'Cargando comidas...' : '-- Selecciona una comida existente --'}
              emptyMessage="No hay comidas disponibles. Crea una nueva."
            />
          </div>
        )}

        {meals.length === 0 ? (
          <div style={{ padding: 12, fontSize: 13, color: '#6b7280', background: '#f9fafb', borderRadius: 6, border: '1px dashed #d1d5db', textAlign: 'center' }}>
            No hay comidas adjuntas (opcional).
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {meals.map(meal => {
              const macros = computeMealMacros(meal);
              return (
                <div key={meal.id} style={{ background: '#f0f9ff', border: '1px solid #e0f2fe', borderRadius: 8, padding: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#0369a1' }}>
                        {meal.name} — {meal.calories} kcal
                      </div>
                      <div style={{ fontSize: 11, color: '#555', marginTop: 2 }}>
                        P {macros.protein}g · C {macros.carbs}g · G {macros.fat}g
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                        {meal.ingredients.map((ing, i) => (
                          <span key={i}>{ing.name} ({ing.quantity}g){i < meal.ingredients.length - 1 ? ', ' : ''}</span>
                        ))}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button
                        type="button"
                        onClick={() => { setEditingMeal(meal); setEditorOpen(true); }}
                        style={{ background: '#e0f2f1', color: '#234e52', border: 'none', borderRadius: 6, padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDetach(meal.id)}
                        style={{ background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 6, padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                      >
                        Quitar
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {patientId && (
        <MealEditorModal
          open={editorOpen}
          patientId={patientId}
          meal={editingMeal}
          onSaved={handleMealSaved}
          onCancel={() => { setEditorOpen(false); setEditingMeal(null); }}
        />
      )}
    </div>
  );
};

export default MealItemForm;
